import { createDomain as createDomain$1, is, hydrate, fork, allSettled, serialize } from 'effector';
export * from 'effector';
import * as React from 'react';
import NextApp from 'next/app';
import cookies from 'next-cookies';
import { Provider } from 'effector-react/ssr';

const domain = createDomain$1("root");

const START_UNIT_KEY = "__EFFECTOR_START_UNIT__";
const INITIAL_STATE_KEY = "__EFFECTOR_INITIAL_STATE__";

function renderPageWithScope(scope, originalRenderPage) {
  return params => {
    // For backwards compatibility
    const enhanceApp = typeof params === "function" ? undefined : params === null || params === void 0 ? void 0 : params.enhanceApp;
    const enhanceComponent = typeof params === "function" ? params : params === null || params === void 0 ? void 0 : params.enhanceComponent;
    const options = {
      enhanceComponent,
      enhanceApp: createEnhanceApp(scope, enhanceApp)
    };
    return originalRenderPage(options);
  };
}

function createEnhanceApp(scope, enhancer) {
  return Component => props => {
    const App = enhancer ? enhancer(Component) : Component;
    return /*#__PURE__*/React.createElement(Provider, {
      value: scope
    }, /*#__PURE__*/React.createElement(App, props));
  };
}

function getStartUnits(originalRenderPage) {
  const units = [];
  originalRenderPage({
    enhanceApp: getStartUnit(units)
  });
  return units.filter(is.unit);
}

function getStartUnit(units) {
  return () => props => {
    var _props$pageProps;

    if (props !== null && props !== void 0 && props[START_UNIT_KEY]) {
      units.push(props[START_UNIT_KEY]);
    }

    if (props !== null && props !== void 0 && (_props$pageProps = props.pageProps) !== null && _props$pageProps !== void 0 && _props$pageProps[START_UNIT_KEY]) {
      units.push(props.pageProps[START_UNIT_KEY]);
    }

    return null;
  };
}

function withHydrate() {
  const isServer = typeof window === "undefined";
  return App => {
    var _class;

    const WithHydrateApp = (_class = class WithHydrateApp extends React.Component {
      constructor(props) {
        super(props);
        if (isServer) return;
        hydrate(domain, {
          values: window.__NEXT_DATA__[INITIAL_STATE_KEY]
        });
      }

      render() {
        return /*#__PURE__*/React.createElement(App, this.props);
      }

    }, _class.origGetInitialProps = App.origGetInitialProps || NextApp.origGetInitialProps, _class.getInitialProps = NextApp.getInitialProps, _class);

    if (App.getInitialProps) {
      WithHydrateApp.getInitialProps = async ctx => {
        const [nextAppProps, hydratedAppProps] = await Promise.all([NextApp.getInitialProps(ctx), App.getInitialProps(ctx)]);
        return { ...nextAppProps,
          ...hydratedAppProps,
          pageProps: Object.assign({}, nextAppProps === null || nextAppProps === void 0 ? void 0 : nextAppProps.pageProps, hydratedAppProps === null || hydratedAppProps === void 0 ? void 0 : hydratedAppProps.pageProps)
        };
      };
    }

    return WithHydrateApp;
  };
}

function withStart(unit) {
  return component => {
    const originalGetInitialProps = component.getInitialProps; // We cancel static optimization for a component

    component.getInitialProps = async ctx => {
      let initialProps = {};

      if (typeof originalGetInitialProps === "function") {
        initialProps = await originalGetInitialProps(ctx);
      }

      return Object.assign({}, initialProps, {
        [START_UNIT_KEY]: unit
      });
    };

    return component;
  };
}

/* eslint-disable no-console, complexity */

function withFork(_temp) {
  let {
    debug,
    serializeIgnore
  } = _temp === void 0 ? {} : _temp;
  return Document => class WithForkDocument extends Document {
    static async getInitialProps(ctx) {
      const originalRenderPage = ctx.renderPage;
      const startUnits = getStartUnits(originalRenderPage);
      if (debug) console.time("1.Domain forked");
      const scope = fork(domain);
      if (debug) console.timeEnd("1.Domain forked");
      ctx.renderPage = renderPageWithScope(scope, originalRenderPage);
      if (debug) console.time("2.All units settled");

      if (startUnits.length > 0) {
        await Promise.all(startUnits.map(unit => allSettled(unit, {
          scope,
          params: {
            req: ctx.req,
            res: ctx.res,
            query: ctx.query,
            cookies: cookies(ctx),
            pathname: ctx.pathname
          }
        })));
      }

      if (debug) console.timeEnd("2.All units settled");
      if (debug) console.time("3.Document.getInitialProps called");
      const initialProps = await Document.getInitialProps(ctx);
      if (debug) console.timeEnd("3.Document.getInitialProps called");
      const initialState = serialize(scope, {
        ignore: serializeIgnore
      });
      if (debug) console.log("4.Received initial state:", initialState);
      return { ...initialProps,
        initialState
      };
    }

    constructor(props) {
      super(props);
      props.__NEXT_DATA__[INITIAL_STATE_KEY] = props.initialState;
    }

    render() {
      return /*#__PURE__*/React.createElement(Document, this.props);
    }

  };
}

const {
  createDomain,
  createStore,
  createEffect,
  createEvent
} = domain;

export { INITIAL_STATE_KEY, START_UNIT_KEY, createDomain, createEffect, createEvent, createStore, domain, withFork, withHydrate, withStart };
//# sourceMappingURL=index.es.js.map
